'use client';

import Image from 'next/image';
import OnlineShopCard from './OnlineShopCard';
import CityNavigateCard from './CityNavigateCard';
import Link from 'next/link';
import HeroFeaturedIn from './HeroFeaturedIn';
import { useState } from 'react';
import { City } from '@/lib/types/customer/City';
import { storeFront } from '@/lib/utils/routeLinks';

type Props = {
  dictionary: any;
  header?: any;
  heroImage?: any;
  videoLink?: any;
  lang: 'en' | 'de';
  data?: any;
};

const HeroSection = ({ dictionary, heroImage, lang, data }: Props) => {
  const sectionData = data?.value;
  const [cityData, setCityData] = useState<City>();

  return (
    <>
      <section className="order-area relative bg-primary-50">
        <div className="container mx-auto px-4 py-6 ">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:gap-6 xl:gap-10 2xl:gap-16">
            <div className="mb-8 flex w-full flex-col lg:mb-0 lg:w-[42%] xl:w-[40%] 2xl:w-[38%]">
              <div className="flex-shrink">
                <h1
                  className="px-3 text-center text-h4 font-black text-[#C32F76] sm:text-h2 lg:px-0 lg:text-left lg:text-[2.5rem]"
                  dangerouslySetInnerHTML={{
                    __html:
                      sectionData?.h1?.[lang] ||
                      dictionary.storefront.home.hero_section.header,
                  }}
                />
                <p
                  className="mb-[15px] block text-center font-medium leading-[140%] text-title sm:text-h5 lg:text-left lg:text-[1.375rem]"
                  dangerouslySetInnerHTML={{
                    __html:
                      sectionData?.desc?.[lang] ??
                      dictionary.storefront.home.hero_section.subheader,
                  }}
                />

                <div className="mt-6 flex justify-center lg:mt-8 lg:justify-start">
                  <div className="w-3/4 lg:w-11/12 xl:w-10/12">
                    <Image
                      src={
                        sectionData?.hero_img_right?.img_path ||
                        heroImage ||
                        '/hero-img.svg'
                      }
                      width={500}
                      height={500}
                      sizes="(max-width: 768px) 75vw, (max-width: 1280px) 40vw, 33vw"
                      alt="Order Burger Area"
                      className="h-auto w-full object-contain"
                      priority
                    />
                  </div>
                </div>
              </div>

              {/* Registration button */}
              <div className="mt-7 flex justify-center lg:justify-start xl:mt-8">
                <Link
                  href={storeFront.register.de()}
                  className="w-auto rounded-full bg-[#FF4D77] px-6 py-2.5 text-center text-base font-semibold text-white transition-all hover:bg-[#E43D66] sm:px-8 sm:py-3 sm:text-lg"
                >
                  {lang === 'en'
                    ? 'Register now - get €10!'
                    : 'Jetzt registrieren - 10 € geschenkt! *'}
                </Link>
              </div>
            </div>

            {/* Second column - City navigation and online shop */}
            <div className="w-full  lg:w-[58%] xl:w-[58%] 2xl:w-[56%]">
              {/* Partners Section - Inside the second column */}
              {sectionData?.partner_section?.visible && (
                <div className="lg:mb-5">
                  <h3 className="text-h5 font-bold text-title lg:pt-1">
                    {sectionData?.partner_section?.heading?.[lang] ??
                      dictionary.storefront.home.hero_section.known_from}
                  </h3>
                  <HeroFeaturedIn
                    partners={sectionData?.partner_section?.images}
                    lang={lang}
                  />
                </div>
              )}
              <div className="lg:mt-8 xl:mt-[60px]">
                <h2 className="mb-3 text-h5 font-bold text-paragraph">
                  {sectionData?.around_your_city_h2?.[lang] ??
                    dictionary.storefront.home.hero_section
                      .online_or_in_your_city}
                </h2>

                {/* Cards container */}
                <div className="flex flex-col space-y-6 lg:flex-row lg:space-x-5 lg:space-y-0">
                  <div className="lg:w-1/2">
                    <OnlineShopCard
                      key={'hero-online-shop-card'}
                      cityThumbUrl={
                        cityData?.media?.image?.[0]?.url ??
                        '/hero-section/online-shop-thumb.jpeg'
                      }
                      cityName={
                        cityData?.raw_name?.[lang] || cityData?.name || ''
                      }
                      countPlaces={cityData?.restaurants_count ?? 0}
                      citySlug={
                        sectionData?.online_shop?.slug ?? `online-shops`
                      }
                      places={dictionary.places}
                    />
                  </div>
                  <div className="lg:w-1/2">
                    <CityNavigateCard
                      lang={lang}
                      cardData={sectionData}
                      setCityData={(resData) =>
                        setCityData(resData?.online_shop)
                      }
                    />
                    <div className="mt-3 xl:mt-5 2xl:mt-4">
                      <Link
                        href="#update"
                        className="whitespace-nowrap text-h5 font-bold text-paragraph underline"
                      >
                        {sectionData?.my_city?.newsletter_text?.[lang] ??
                          dictionary.storefront.home.hero_section
                            .subscribe_to_the_newsletter}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* QR Code Section */}
              {sectionData?.qr_code?.visible && (
                <div className="qr-app-section mt-8 flex items-start justify-start space-x-5">
                  <div className="">
                    <Image
                      src={sectionData?.qr_code?.img_path ?? '/qr-code.png'}
                      width={180}
                      height={184}
                      alt={sectionData?.qr_code?.label?.[lang] ?? 'QR Code'}
                    />
                  </div>
                  <div className="-mb-4">
                    <h3 className="mb-3 mt-2 text-h5 font-bold text-title">
                      {sectionData?.app_store_h2?.[lang] ??
                        dictionary.storefront.home.hero_section.get_the_app}
                    </h3>
                    <div className="flex flex-col space-y-4">
                      <Link
                        href={sectionData?.app_store_link ?? '/'}
                        target="_blank"
                        className=""
                      >
                        <Image
                          src="/app-store.png"
                          width={160}
                          height={50}
                          alt="App Store"
                        />
                      </Link>
                      <Link
                        href={sectionData?.play_store_link ?? '/'}
                        target="_blank"
                        className=""
                      >
                        <Image
                          src="/play-store.png"
                          width={160}
                          height={50}
                          alt="Play Store"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
